/**
 * HOW AN ARTICLE'S OFFER LINKS ARE MEASURED, in one place.
 *
 * Clicks need no event of their own: PostHog autocapture already records every
 * click with all of the element's attributes, so each link carries a stable
 * `data-attr` label and the click is read back by that label (context/
 * analytics.md, "no per-button click event"). Class strings change on any
 * restyle; these labels do not.
 *
 * "Was it seen" is the one thing autocapture cannot answer, so the three
 * placements whose visibility is genuinely unknown carry `data-cta-seen`, and
 * one client leaf (components/marketing/article-cta-seen.tsx) fires `cta_viewed`
 * once per page view for each. The mobile sticky bar is always on screen on a
 * phone and the desktop rail is on screen from load, so a view event there
 * would only repeat the pageview; the bar gets a click label and no seen mark.
 *
 * A plain module, not a client file: server components read these values, and a
 * value imported from a "use client" file arrives as a client reference.
 */

export const CTA_SEEN_ATTR = "data-cta-seen"

export const CtaSeenPlacement = {
  ClosingBand: "closing_band",
  NextStepRail: "next_step_rail",
  InlineOffer: "inline_offer",
} as const

export type CtaSeenPlacementValue = (typeof CtaSeenPlacement)[keyof typeof CtaSeenPlacement]

const SEEN_VALUES: readonly string[] = Object.values(CtaSeenPlacement)

/** The click labels, the values of `data-attr`. */
export const CtaLabel = {
  StickyBar: "cta-sticky-bar",
  ClosingBand: "cta-closing-band",
  RailOffer: "cta-rail-offer",
  RailSituation: "cta-rail-situation",
  RailCourse: "cta-rail-course",
  InlineOffer: "cta-inline-offer",
} as const

export function seenAttr(placement: CtaSeenPlacementValue): Record<string, string> {
  return { [CTA_SEEN_ATTR]: placement }
}

export function isSeenPlacement(value: string | null | undefined): value is CtaSeenPlacementValue {
  return typeof value === "string" && SEEN_VALUES.includes(value)
}

/** A reporter that passes each placement on once and ignores repeats and
 *  anything that is not a known placement. The rail is in the page twice (side
 *  rail and inline), so one placement can be reached from two elements. */
export function createSeenOnce(report: (placement: CtaSeenPlacementValue) => void) {
  const seen = new Set<CtaSeenPlacementValue>()
  return (raw: string | null | undefined): boolean => {
    if (!isSeenPlacement(raw) || seen.has(raw)) return false
    seen.add(raw)
    report(raw)
    return true
  }
}
